import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Button, Card, Space, message, Tooltip, Spin, Switch, Alert } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;

// Formül yardımcı metni - lastN() fonksiyonu eklendi
const formulaHelpText = (
  <div>
    <p>Formül yazarken aşağıdaki fonksiyonları kullanabilirsiniz:</p>
    <ul>
      <li><code>sum(metrikAdı)</code> - Günlük metriğin aylık toplamını hesaplar</li>
      <li><code>avg(metrikAdı)</code> - Günlük metriğin aylık ortalamasını hesaplar</li>
      <li><code>lastDays(metrikAdı, günSayısı)</code> - Son X gününün ortalaması</li>
      <li><code>monthlyAvg(metrikAdı, aySayısı)</code> - Son X ay ortalaması</li>
      <li><code>monthlyChange(metrikAdı)</code> - Önceki aya göre değişim yüzdesi</li>
      <li><code><strong>last1(metrikAdı), last2(metrikAdı), last11(metrikAdı)</strong></code> - N ay önceki değer (YENİ!)</li>
    </ul>
    <p>Özel Değişkenler:</p>
    <ul>
      <li><code>CURRENT_DAY</code> - Ayın kaçıncı günü</li>
      <li><code>REMAINING_DAYS</code> - Ayın kalan gün sayısı</li>
      <li><code>TOTAL_DAYS</code> - Ayın toplam gün sayısı</li>
    </ul>
    <p>Forecast Formül Örnekleri:</p>
    <ul>
      <li><code>last1(CIRO) * 1.1</code> - Geçen ay cirosunun %10 fazlası</li>
      <li><code>monthlyAvg(CIRO, 3) + (last1(CIRO) - last2(CIRO))</code> - 3 aylık ortalama + trend</li>
      <li><code>last11(CIRO) * 1.2</code> - Geçen yılın aynı ayının %20 fazlası</li>
      <li><code>(last1(CIRO) + last2(CIRO) + last3(CIRO)) / 3</code> - Son 3 ayın ortalaması</li>
    </ul>
  </div>
);

const ForecastForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [loadingInitialData, setLoadingInitialData] = useState(true);
    const [brands, setBrands] = useState([]);
    const [allMonthlyMetrics, setAllMonthlyMetrics] = useState([]);
    const [loadingMetrics, setLoadingMetrics] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isTemplateMode, setIsTemplateMode] = useState(false);
    const [selectedBrandId, setSelectedBrandId] = useState(null);

    const templates = ['WEB AUTOBUDGET', 'WEB MANUELBUDGET', 'TRENDYOL'];

    const loadMonthlyMetrics = useCallback(async () => {
        setLoadingMetrics(true);
        try {
            const response = await api.get('/monthly-metrics', { params: { minimal: 'true' } });
            const metrics = response.data
                ?.filter(metric => metric && !metric.isDeleted)
                .map(metric => ({
                    id: parseInt(metric.id),
                    name: metric.name
                })) || [];
            setAllMonthlyMetrics(metrics);
        } catch (error) {
            console.error('Aylık metrikler yüklenirken hata:', error);
            message.error('Aylık metrikler yüklenirken bir hata oluştu');
            setAllMonthlyMetrics([]);
        } finally {
            setLoadingMetrics(false);
        }
    }, []);

    const loadBrands = useCallback(async () => {
        try {
            const response = await api.get('/brands');
            const sortedBrands = response.data
                ?.filter(brand => brand && brand.name)
                .sort((a, b) => a.name.localeCompare(b.name)) || [];
            setBrands(sortedBrands);
        } catch (error) {
            console.error('Markalar yüklenirken hata:', error);
            message.error('Markalar yüklenirken bir hata oluştu');
        }
    }, []);

    const loadForecastSetting = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            const response = await api.get(`/forecast/${id}`);
            const setting = response.data;

            if (!setting) {
                throw new Error('Forecast ayarı bulunamadı');
            }

            // Form alanlarını doldur
            form.setFieldsValue({
                brandId: setting.brandId || null,
                template: setting.template || null,
                formula: setting.formula,
                dependentMetrics: setting.dependentMetrics || [],
                description: setting.description || '',
                targetMetricId: setting.targetMetricId || null,
                autoSave: setting.autoSave || false
            });

            // Mod ayarları
            setIsTemplateMode(!setting.brandId);
            setSelectedTemplate(setting.template);
            setSelectedBrandId(setting.brandId);

        } catch (error) {
            console.error('Forecast ayarı yüklenirken hata:', error);
            message.error('Forecast ayarı yüklenirken bir hata oluştu');
            navigate('/forecast');
        } finally {
            setLoading(false);
        }
    }, [id, form, navigate]);

    useEffect(() => {
        let mounted = true;
        setLoadingInitialData(true);

        const loadInitialData = async () => {
            try {
                await Promise.all([
                    loadBrands(),
                    loadMonthlyMetrics()
                ]);

                if (id && mounted) {
                    await loadForecastSetting();
                }
            } catch (error) {
                console.error('İlk veri yükleme hatası:', error);
            } finally {
                if (mounted) {
                    setLoadingInitialData(false);
                }
            }
        };

        loadInitialData();

        return () => {
            mounted = false;
        };
    }, [id, loadBrands, loadMonthlyMetrics, loadForecastSetting]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            if (!values.formula?.trim()) {
                message.error('Formül boş olamaz');
                return;
            }

            if (isTemplateMode && !selectedTemplate) {
                message.error('Template modu için template seçimi gerekli');
                return;
            }

            if (!isTemplateMode && !values.brandId) {
                message.error('Marka modu için marka seçimi gerekli');
                return;
            }

            const formData = {
                brandId: isTemplateMode ? null : values.brandId,
                template: isTemplateMode ? selectedTemplate : null,
                formula: values.formula.trim(),
                dependentMetrics: values.dependentMetrics || [],
                description: values.description || null,
                targetMetricId: values.targetMetricId || null,
                autoSave: values.autoSave || false
            };

            let response;
            if (isTemplateMode) {
                // Template bazında toplu uygulama
                response = await api.post(`/forecast/template/${selectedTemplate}`, formData);
                message.success(`${selectedTemplate} template'ine sahip markalar için forecast ayarları uygulandı`);
            } else {
                // Brand-specific ayar
                if (id) {
                    response = await api.put(`/forecast/${id}`, formData);
                    message.success('Forecast ayarı başarıyla güncellendi');
                } else {
                    response = await api.post(`/forecast/brands/${values.brandId}`, formData);
                    message.success('Forecast ayarı başarıyla oluşturuldu');
                }
            }

            if (response.status === 200 || response.status === 201) {
                setTimeout(() => navigate('/forecast'), 500);
            }
        } catch (error) {
            console.error('Form gönderme hatası:', error);
            const errorMsg = error.response?.data?.error || error.message;
            message.error('İşlem sırasında bir hata oluştu: ' + errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const filteredBrands = selectedTemplate
        ? brands.filter(brand => brand.template === selectedTemplate)
        : brands;

    if (loadingInitialData) {
        return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" tip="Veriler yükleniyor..." /></div>;
    }

    return (
        <div style={{ padding: 24 }}>
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{id ? 'Forecast Ayarı Düzenle' : 'Yeni Forecast Ayarı'}</span>
                    </div>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        dependentMetrics: [],
                        autoSave: false,
                        targetMetricId: null
                    }}
                >
                    <Card title="Uygulama Modu" size="small" style={{ marginBottom: 16 }}>
                        <Form.Item
                            label="Template Modu"
                            tooltip="Template modu: Seçili template'e sahip tüm markalara aynı formülü uygula"
                        >
                            <Switch
                                checked={isTemplateMode}
                                onChange={setIsTemplateMode}
                                checkedChildren="Template"
                                unCheckedChildren="Marka"
                                disabled={!!id} // Düzenleme modunda değiştirilemez
                            />
                        </Form.Item>

                        {isTemplateMode ? (
                            <Form.Item
                                label="Template Seçin"
                                rules={[{ required: true, message: 'Template seçin' }]}
                            >
                                <Select
                                    value={selectedTemplate}
                                    onChange={setSelectedTemplate}
                                    placeholder="Template seçin"
                                >
                                    {templates.map(template => (
                                        <Option key={template} value={template}>
                                            {template}
                                        </Option>
                                    ))}
                                </Select>
                                {selectedTemplate && (
                                    <Alert
                                        message={`${filteredBrands.length} marka bu template'e sahip`}
                                        type="info"
                                        showIcon
                                        style={{ marginTop: 8 }}
                                    />
                                )}
                            </Form.Item>
                        ) : (
                            <Form.Item
                                name="brandId"
                                label="Marka Seçin"
                                rules={[{ required: true, message: 'Marka seçin' }]}
                            >
                                <Select
                                    placeholder="Marka seçin"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={setSelectedBrandId}
                                >
                                    {brands.map(brand => (
                                        <Option key={brand.id} value={brand.id}>
                                            {brand.name} {brand.template ? `(${brand.template})` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </Card>

                    <Form.Item
                        name="formula"
                        label={
                            <span>
                                Forecast Formülü
                                <Tooltip 
                                    overlayInnerStyle={{ width: '800px', fontSize: '12px', maxWidth: '800px' }} 
                                    title={formulaHelpText}
                                >
                                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                                </Tooltip>
                            </span>
                        }
                        rules={[{ required: true, message: 'Formül gerekli' }]}
                    >
                        <TextArea
                            placeholder="Örnek: last1(CIRO) * 1.1 veya monthlyAvg(CIRO, 3)"
                            rows={4}
                        />
                    </Form.Item>

                    <Form.Item
                        name="dependentMetrics"
                        label="Bağımlı Aylık Metrikler"
                        rules={[{ required: true, message: 'En az bir aylık metrik seçin' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Formülde kullanılacak aylık metrikleri seçin"
                            style={{ width: '100%' }}
                            loading={loadingMetrics}
                            optionFilterProp="label"
                            showSearch={true}
                            filterOption={(input, option) =>
                                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            notFoundContent={loadingMetrics ? <Spin size="small" /> : "Metrik bulunamadı"}
                        >
                            {allMonthlyMetrics.map(metric => (
                                <Select.Option
                                    key={metric.id}
                                    value={metric.id}
                                    label={metric.name}
                                >
                                    {metric.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Açıklama"
                    >
                        <TextArea
                            placeholder="Forecast formülü hakkında açıklama"
                            rows={3}
                            maxLength={500}
                        />
                    </Form.Item>

                    <Card title="Otomatik Kaydetme Ayarları" size="small" style={{ marginBottom: 16 }}>
                        <Form.Item
                            name="autoSave"
                            label="Forecast Sonucunu Otomatik Kaydet"
                            valuePropName="checked"
                            tooltip="İşaretlenirse, hesaplanan forecast değeri seçilen aylık metriğe otomatik olarak kaydedilir"
                        >
                            <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
                        </Form.Item>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                                prevValues.autoSave !== currentValues.autoSave
                            }
                        >
                            {({ getFieldValue }) =>
                                getFieldValue('autoSave') ? (
                                    <Form.Item
                                        name="targetMetricId"
                                        label="Hedef Aylık Metrik"
                                        rules={[
                                            {
                                                required: getFieldValue('autoSave'),
                                                message: 'Otomatik kaydetme için hedef metrik seçilmelidir'
                                            }
                                        ]}
                                        tooltip="Forecast sonucu bu metriğe kaydedilecek"
                                    >
                                        <Select
                                            placeholder="Forecast sonucunun kaydedileceği aylık metriği seçin"
                                            loading={loadingMetrics}
                                            showSearch
                                            filterOption={(input, option) =>
                                                option?.children?.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {allMonthlyMetrics.map(metric => (
                                                <Option key={metric.id} value={metric.id}>
                                                    {metric.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                ) : null
                            }
                        </Form.Item>
                    </Card>

                    <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f6f6f6', borderRadius: 8 }}>
                        <h4>Formül Örnekleri:</h4>
                        <ul style={{ margin: 0, fontSize: '12px' }}>
                            <li><code>last1(CIRO) * 1.15</code> - Geçen ayın %15 fazlası</li>
                            <li><code>monthlyAvg(CIRO, 6)</code> - Son 6 aylık ortalama</li>
                            <li><code>last11(CIRO) + (last1(CIRO) - last12(CIRO))</code> - Geçen yılın aynı ayı + yıllık trend</li>
                            <li><code>(last1(REVENUE) + last2(REVENUE)) / 2 * 1.1</code> - Son 2 ayın ortalamasının %10 fazlası</li>
                        </ul>
                    </div>

                    <Form.Item style={{ marginTop: 24 }}>
                        <Space>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={loading}
                                size="large"
                            >
                                {id ? 'Forecast Ayarını Güncelle' : 'Forecast Ayarını Oluştur'}
                            </Button>
                            <Button 
                                onClick={() => navigate('/forecast')} 
                                disabled={loading}
                                size="large"
                            >
                                İptal
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default ForecastForm;